import { FilingFormState } from '../filing-form-state.service';

export type Dimension = 'Consolidated' | 'Separate';
export type Period = 'CurrentYear' | 'PreviousYear';

export interface VisibilityCell {
  dimension: Dimension;
  period: Period;
  visible: boolean;
}

export interface FilingSlices {
  filingPrerequisites?: Record<string, unknown>;
  filingInfo?: Record<string, unknown>;
}

/** A sheet's visibility config. A null axis means that axis does not apply:
 *  - consolidatedGate null → Consolidated always shown
 *  - periodGate null       → CurrentYear-only (roles 23, 24) */
export interface DimensionVisibilityConfig {
  consolidatedGate: { field: string; consolidatedValue: string } | null;
  periodGate: { field: string; firstTimeValue: string } | null;
}

/** Prereq-first, then filingInfo. An empty string / null / undefined in the
 *  prereq slice falls through to filingInfo. The ONE resolution rule shared by
 *  the mounted and headless paths. */
export function resolvePrerequisiteValue(field: string, slices: FilingSlices): unknown {
  const fromPrereqs = slices.filingPrerequisites?.[field];
  if (fromPrereqs !== undefined && fromPrereqs !== null && fromPrereqs !== '') {
    return fromPrereqs;
  }
  return slices.filingInfo?.[field];
}

/** Pull a cross-sheet slice from a pure validator's context map into FilingSlices shape. */
export function readSlice(
  crossSheetData: ReadonlyMap<keyof FilingFormState, Record<string, any>> | undefined,
  key: keyof FilingFormState,
): Record<string, unknown> | undefined {
  return crossSheetData?.get(key) as Record<string, unknown> | undefined;
}

const DIMENSIONS: ReadonlyArray<Dimension> = ['Consolidated', 'Separate'];

export function resolveDimensionVisibility(config: DimensionVisibilityConfig, slices: FilingSlices): VisibilityCell[] {
  // Consolidated gate: no gate → always shown; unknown/empty value → shown
  // (pre-subscription default); explicit value → must equal consolidatedValue.
  let showCons = true;
  if (config.consolidatedGate) {
    const value = resolvePrerequisiteValue(config.consolidatedGate.field, slices);
    showCons = (value === undefined || value === null || value === '') ? true : value === config.consolidatedGate.consolidatedValue;
  }

  // Period gate: no gate → CurrentYear-only; first-time prep → no previous;
  // any other value (subsequent OR unknown) → previous shown.
  const periodApplies = config.periodGate !== null;
  let hasPrev = false;
  if (config.periodGate) {
    const value = resolvePrerequisiteValue(config.periodGate.field, slices);
    hasPrev = value !== config.periodGate.firstTimeValue;
  }

  const periods: ReadonlyArray<Period> = periodApplies ? ['CurrentYear', 'PreviousYear'] : ['CurrentYear'];

  const cells: VisibilityCell[] = [];
  for (const dimension of DIMENSIONS) {
    for (const period of periods) {
      const isValidDimension = dimension === 'Consolidated' ? showCons : true;
      const isValidPeriod = period === 'CurrentYear' ? true : hasPrev;
      cells.push({ dimension, period, visible: isValidDimension && isValidPeriod });
    }
  }
  return cells;
}

export function isCellVisible(cells: VisibilityCell[], dimension: Dimension, period: Period): boolean {
  return cells.some(cell => cell.dimension === dimension && cell.period === period && cell.visible);
}

export function activeDimensions(cells: VisibilityCell[]): Dimension[] {
  return DIMENSIONS.filter(dimension => cells.some(cell => cell.dimension === dimension && cell.visible));
}

export function showConsolidated(cells: VisibilityCell[]): boolean {
  return isCellVisible(cells, 'Consolidated', 'CurrentYear');
}

export function hasPreviousYear(cells: VisibilityCell[]): boolean {
  return cells.some(cell => cell.period === 'PreviousYear' && cell.visible);
}
